<template>
    <AdminPageInner v-if="loaded" :breadcrumbs="breadcrumbs">
        <template #heading>Application History {{ user.name ? ` - ${user.name}` : '' }}</template>

        <PaginationWrapper
            ref="pagination"
            class="mt-4"
            :rows="rows"
            :constraints="constraints"
            :meta="meta">
            <DataComponent
                class="w-full mt-6"
                :data="rows"
                :initialSort="initialSort"
                @sort="onSort">
                <DataField name="course_title" label="Course" class="md:w-1/4" v-slot="{row, sortable}">
                    <AppLink
                        v-if="row.session_group.course.user_can.view"
                        :to="{name: 'admin.courses.show', params: {courseId: row.session_group.course.id}}">
                        {{ row.session_group.course.title }}
                    </AppLink>
                </DataField>
                <DataField name="start_date" label="Group" v-slot="{row}">
                    <AppLink
                        v-if="row.session_group.user_can.view"
                        :to="{
                            name: 'admin.courses.session-groups.show',
                            params: {
                                courseId: row.session_group.course.id,
                                courseSessionGroupId: row.session_group.id
                            }
                        }">
                        <CalendarFormat :date="row.session_group.start_date" :showState="false"/>
                    </AppLink>
                    <CalendarFormat v-else :date="row.session_group.start_date" :showState="false"/>
                </DataField>
                <DataField name="status" label="Application Status" v-slot="{row}">
                    <ApplicationStatus :status="row.status" class="text-xs"/>
                </DataField>
                <DataField name="sessions" label="Sessions" :sortable="false" v-slot="{row}">
                    <ul>
                        <li v-for="session in sessions(row)" class="flex items-center text-sm">
                            <template v-if="session.status === RecordStatus.Pending">
                                <font-awesome-icon icon="clock" class="text-gray-600 mr-2"/>
                                <span>{{ session.title }}</span>
                            </template>
                            <template v-if="session.status === RecordStatus.Attended">
                                <font-awesome-icon icon="check" class="text-green-600 mr-2"/>
                                <span>{{ session.title }}</span>
                            </template>
                            <template v-if="session.status === RecordStatus.Absent">
                                <font-awesome-icon icon="times" class="text-red-600 mr-2"/>
                                <span>{{ session.title }}</span>
                            </template>
                            <template v-if="session.status === RecordStatus.Cancelled">
                                <font-awesome-icon icon="times" class="text-red-600 mr-2"/>
                                <span class="line-through">{{ session.title }}</span>
                            </template>
                        </li>
                    </ul>
                </DataField>
                <DataField
                    name="completed"
                    label="Completed"
                    class="md:w-4 text-right"
                    :sortable="false"
                    v-slot="{row}">
                    <font-awesome-icon v-if="row.completed" icon="check" class="text-green-600"/>
                    <font-awesome-icon v-else icon="times" class="text-red-600"/>
                </DataField>

                <!--                <DataField name="email" class="md:w-1/4">-->
                <!--                    <template v-slot="{row, sortable}">-->
                <!--                        {{ row.email }}-->
                <!--                    </template>-->
                <!--                </DataField>-->
            </DataComponent>
        </PaginationWrapper>
    </AdminPageInner>
</template>

<script>

import axios from "axios";
import AdminPageInner from "@/Components/Layout/AdminPageInner.vue";
import PaginationWrapper from "@/Components/Layout/PaginationWrapper.vue";
import PaginationPage from "@/Mixins/PaginationPage";
import {DataComponent, DataField} from "@/Components/DataComponent";
import CalendarFormat from "@/Components/CalendarFormat";
import Breadcrumbs from "@/Components/Breadcrumbs";
import AppLink from "@/Components/AppLink";
import ApplicationStatus from "@/Components/ApplicationStatus";
import {CourseApplicationStatus, CourseRecordStatus as RecordStatus} from "@/Lib/Enums";

export default {

    name: "UserApplicationsIndexPage",

    data() {
        return {
            RecordStatus,
            user: {}
        };
    },

    mixins: [PaginationPage],

    methods: {
        setData({applications, user}) {
            PaginationPage.methods.setData.call(this, applications);
            if (user) {
                this.user = user;
            }
        },
        sessions(application) {
            return application.session_group.sessions.map(session => {
                const record = application.records.find(item => item.course_session_id === session.id);

                let status;

                if (record) {
                    status = record.attended ? RecordStatus.Attended : RecordStatus.Absent;
                } else if (
                    application.status === CourseApplicationStatus.Accepted ||
                    application.status === CourseApplicationStatus.Pending
                ) {
                    status = RecordStatus.Pending
                } else {
                    status = RecordStatus.Cancelled
                }

                return {
                    title: session.title || 'Session',
                    status,
                };
            });
        },
    },

    computed: {
        breadcrumbs() {
            return [
                {label: 'Admin', to: {name: 'admin'}},
                {label: 'Users', to: {name: 'admin.users.index'}},
                {
                    label: `${this.user.name || 'User'}`,
                    to: {
                        name: 'admin.users.show',
                        params: {userId: this.$route.params.userId}
                    }
                },
                {label: 'Application History', to: {name: this.$route.name}},
            ];
        },
    },

    components: {
        AdminPageInner,
        PaginationWrapper,
        DataComponent,
        DataField,
        CalendarFormat,
        Breadcrumbs,
        ApplicationStatus,
        AppLink
    },

    async beforeRouteEnter(to, from, next) {

        const data = await fetchAllData(to);

        next(vm => vm.setData(data));
    },

    async beforeRouteUpdate(to, from) {

        let data = {};

        if (to.params.userId !== from.params.userId) {
            data = fetchAllData(to);
        } else {
            data.applications = (await fetchApplications(to)).data;
        }

        this.setData(data);
    },
};

const fetchAllData = async to => {

    const data = await Promise.all([
        fetchApplications(to),
        axios.get(route('api.users.show', to.params.userId)),
    ])

    return {
        applications: data[0].data,
        user: data[1].data
    };
}

const fetchApplications = async (to) => {
    return axios.get(route('api.users.applications', to.params.userId), {
        params: Object.assign(to.query)
    });
}

</script>
